import { STORAGE_KEYS, PROGRESS_MAX_AGE_MS } from "@/constants";
import { idbDelete, VIDEO_KEY } from "./idb";

/**
 * Resume support: the small half of the flow state, persisted to localStorage.
 *
 * The split matters. Small values (the face still, the signature PNG, a video poster
 * frame and a step pointer) go here; the recorded video blob goes to IndexedDB, because
 * localStorage would reject it outright. See `services/idb.js`.
 *
 * Three behaviours are load-bearing and easy to lose in a rewrite:
 *  1. reads used for merging must NOT apply the expiry, or saving mid-session can wipe
 *     the very record being extended;
 *  2. every save refreshes the timestamp, so an active session never self-expires;
 *  3. under quota pressure we shed the heaviest optional field first and keep the step
 *     pointer last — a resume without a thumbnail is still a resume.
 */

/** Read the stored record with no expiry side-effect. Used when merging on save. */
function readRaw() {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.progress);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch (e) {
    return null;
  }
}

/**
 * Resume-time read: applies the one-hour expiry. This is the only place that discards
 * a stored record for being stale.
 */
export function loadProgress() {
  const stored = readRaw();
  if (!stored || !stored.ts || Date.now() - stored.ts > PROGRESS_MAX_AGE_MS) {
    if (stored) clearProgress();
    return null;
  }
  return stored;
}

/**
 * Merge a patch into the stored record.
 *
 * Degrades in two steps if localStorage is full rather than throwing away the session:
 * first drop the video poster (by far the largest field), then fall back to the pointer
 * plus the signature.
 */
export function saveProgress(patch) {
  const next = Object.assign({}, readRaw(), patch, { ts: Date.now() });

  const persist = (value) => localStorage.setItem(STORAGE_KEYS.progress, JSON.stringify(value));

  try {
    persist(next);
    return true;
  } catch (e) {
    /* over quota — fall through and shed weight */
  }

  try {
    const slim = Object.assign({}, next);
    delete slim.videoPoster;
    persist(slim);
    return true;
  } catch (e) {
    /* still over quota — keep only what a resume strictly needs */
  }

  try {
    persist({
      resumeAt: next.resumeAt,
      hasVideo: next.hasVideo,
      signature: next.signature,
      ts: next.ts,
    });
    return true;
  } catch (e) {
    return false;
  }
}

/** Drop the saved record and the cached video blob together. */
export function clearProgress() {
  try {
    localStorage.removeItem(STORAGE_KEYS.progress);
  } catch (e) {
    /* nothing to do if storage is unavailable */
  }
  return idbDelete(VIDEO_KEY);
}
